import React, { FC, useState, useRef } from "react";
import {
	Box,
	InputAdornment,
	TextField,
	FormHelperText,
	useTheme,
	useMediaQuery,
} from "@material-ui/core";
import { Project } from "domain-entities";
import SearchIcon from "@material-ui/icons/Search";
import { px } from "Configs/theme";
import { useTranslation } from "Providers/TranslationProvider";
import {
	getSearchValues,
	getMatchingProjectTrees,
} from "./ProjectSearchHelper";
import ProjectSubHeaderList from "./ProjectSubHeaderList";
import ProjectTreeList from "./ProjectList";
import { FieldError } from "react-hook-form";
import { useActiveProjectTrees } from "./hooks/useActiveProjectTrees";

interface Props {
	onClickListItem: (project: Project) => void;
	autoFocus?: boolean;
	fieldError?: FieldError;
}

const ProjectSearchView: FC<Props> = (props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const projectTrees = useActiveProjectTrees();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const [searchValues, setSearchValues] = useState<string[]>([]);
	const searchTermRef = useRef("");

	function setDebouncedSearchValues(searchTerm: string) {
		searchTermRef.current = searchTerm;
		setTimeout(async () => {
			if (searchTermRef.current === searchTerm) {
				setSearchValues(getSearchValues(searchTerm));
			}
		}, 500);
	}

	function renderProjectList() {
		if (searchValues.length) {
			const matchingProjectTrees = getMatchingProjectTrees(
				projectTrees,
				searchValues
			);

			return (
				<ProjectSubHeaderList
					projectTrees={matchingProjectTrees}
					onClickListItem={props.onClickListItem}
				/>
			);
		} else {
			return (
				<ProjectTreeList
					projectTrees={projectTrees}
					onClickListItem={props.onClickListItem}
				/>
			);
		}
	}

	return (
		<Box mt={3}>
			<TextField
				variant="outlined"
				error={!!props.fieldError}
				label={
					isSmallScreen
						? t("ProjectSearchView_Label_SearchShort")
						: t("ProjectSearchView_Label_SearchLong")
				}
				autoFocus={props.autoFocus}
				onChange={(event) => setDebouncedSearchValues(event.target.value)}
				fullWidth
				InputProps={{
					endAdornment: (
						<InputAdornment position="end">
							<SearchIcon />
						</InputAdornment>
					),
				}}
			/>
			{props.fieldError && (
				<FormHelperText error={true}>{props.fieldError.message}</FormHelperText>
			)}
			<Box mt={px.xl} boxShadow={3}>
				{renderProjectList()}
			</Box>
		</Box>
	);
};

export default ProjectSearchView;
